// Straightforward API calls based on the API specification

import { Product, Client, Sale } from './db';
import { supabase } from './supabaseClient';

const API_BASE_URL = process.env.API_BASE_URL || 'https://cosecha-tropical-server.fly.dev/api';


const API_PASSWORD = 'cosecha-tropical-2025';

// Helper function for JSON API calls
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-api-password': API_PASSWORD,
      ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  const result = await response.json();
  return result.data;
}

// Helper function for multipart/form-data API calls (let browser set Content-Type + boundary)
async function apiRequestMultipart<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'x-api-password': API_PASSWORD,
      ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
      // No Content-Type — browser sets multipart/form-data with boundary automatically
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  const result = await response.json();
  return result.data;
}

function productToFormData(product: Partial<Omit<Product, 'id'>>, imageFile?: File): FormData {
  const fd = new FormData();
  if (product.name !== undefined) fd.append('name', product.name);
  if (product.stock !== undefined) fd.append('stock', String(product.stock));
  if (product.price !== undefined) fd.append('price', String(product.price));
  if (product.cost !== undefined) fd.append('cost', String(product.cost));
  if (product.brand !== undefined) fd.append('brand', product.brand ?? '');
  if (imageFile) fd.append('image', imageFile);
  return fd;
}

// Products API
export const productsAPI = {
  getAll: (): Promise<Product[]> => apiRequest<Product[]>('/products'),
  getById: (id: number): Promise<Product> => apiRequest<Product>(`/products/${id}`),
  create: (product: Omit<Product, 'id'>, imageFile?: File): Promise<Product> =>
    apiRequestMultipart<Product>('/products', {
      method: 'POST',
      body: productToFormData(product, imageFile),
    }),
  update: (id: number, product: Partial<Omit<Product, 'id'>>, imageFile?: File): Promise<Product> =>
    apiRequestMultipart<Product>(`/products/${id}`, {
      method: 'PUT',
      body: productToFormData(product, imageFile),
    }),
  delete: (id: number): Promise<void> => apiRequest<void>(`/products/${id}`, {
    method: 'DELETE',
  }),
};

// Clients API
export const clientsAPI = {
  getAll: (): Promise<Client[]> => apiRequest<Client[]>('/clients'),
  getById: (id: number): Promise<Client> => apiRequest<Client>(`/clients/${id}`),
  create: (client: Omit<Client, 'id'>): Promise<Client> => apiRequest<Client>('/clients', {
    method: 'POST',
    body: JSON.stringify(client),
  }),
  update: (id: number, client: Partial<Client>): Promise<Client> => apiRequest<Client>(`/clients/${id}`, {
    method: 'PUT',
    body: JSON.stringify(client),
  }),
  delete: (id: number): Promise<void> => apiRequest<void>(`/clients/${id}`, {
    method: 'DELETE',
  }),
};

// Sales API
export const salesAPI = {
  getAll: (): Promise<any[]> => apiRequest<any[]>('/sales'),
  getById: (id: number): Promise<any> => apiRequest<any>(`/sales/${id}`),
  create: (sale: any): Promise<any> => apiRequest<any>('/sales', {
    method: 'POST',
    body: JSON.stringify(sale),
  }),
  update: (id: number, sale: any): Promise<any> => apiRequest<any>(`/sales/${id}`, {
    method: 'PUT',
    body: JSON.stringify(sale),
  }),
  delete: (id: number): Promise<void> => apiRequest<void>(`/sales/${id}`, {
    method: 'DELETE',
  }),
  updateProductPayment: (saleId: number, productId: number, quantityPaid: number): Promise<any> => 
    apiRequest<any>(`/sales/${saleId}/products/${productId}/payment`, {
      method: 'PUT',
      body: JSON.stringify({ quantity_paid: quantityPaid }),
    }),
  returnProductQuantity: (saleId: number, productId: number, quantity: number): Promise<any> =>
    apiRequest<any>(`/sales/${saleId}/products/${productId}/${quantity}`, {
      method: 'DELETE',
    }),
}; 